import { useState } from 'react'
import { formatTimeRange, readableDescription, toMapLink } from '../lib/searchUtils.js'
import { getEventPinConfig, getPlacePinConfig } from '../lib/pinConfig.js'
import ImageCarousel from './ImageCarousel.jsx'
import SaveButton from './SaveButton.jsx'
import PlanButton from './PlanButton.jsx'

export default function SearchResultCard({
    item,
    isSelected,
    onSelect,
    selectedDate,
    isSaved,
    onToggleSave,
    itemPlanColors = [],
    onShowPlanPopup,
    variant = 'default'
}) {
    const [copySuccess, setCopySuccess] = useState(false)

    // Check for null item AFTER all hooks
    if (!item) {
        return null
    }

    const categoryLabel = item?.category ?? (item?.searchType === 'places' ? 'Place' : 'Event')
    const pinConfig = item?.searchType === 'places'
        ? getPlacePinConfig(item?.category ?? item?.ml_category)
        : getEventPinConfig(item?.category ?? item?.ml_category)
    const description = readableDescription(item?.description)

    // Truncate description to a smaller size (around 170 characters)
    // Accounts for wide characters and truncates at word boundaries
    const truncateText = (text, maxLength = 170) => {
        if (!text) return text

        // Calculate weighted length (wide characters count as more)
        const getWeightedLength = (str) => {
            let weight = 0
            for (const char of str) {
                // Wide characters (M, W, etc.) and uppercase count as 1.2
                // Numbers and punctuation count as 0.8
                // Special characters count as 1.3
                if (/[MW@#$%&]/.test(char)) {
                    weight += 1.3
                } else if (/[A-Z]/.test(char)) {
                    weight += 1.1
                } else if (/[0-9.,;:!?]/.test(char)) {
                    weight += 0.8
                } else {
                    weight += 1
                }
            }
            return weight
        }

        // If text is short enough, return as-is
        if (getWeightedLength(text) <= maxLength) return text

        // Truncate at word boundary
        let truncated = ''
        const words = text.split(/(\s+)/)

        for (const word of words) {
            const testString = truncated + word
            if (getWeightedLength(testString) > maxLength) {
                break
            }
            truncated = testString
        }

        // If we got nothing (first word too long), just slice at maxLength
        if (!truncated.trim()) {
            truncated = text.slice(0, maxLength)
        }

        return truncated.trim() + '...'
    }
    const truncatedDescription = truncateText(description)

    // Determine searchType from item.type if searchType is not set
    const searchType = item?.searchType || (item?.type === 'event' ? 'events' : item?.type === 'place' ? 'places' : null)

    const timeRange = searchType === 'events'
        ? formatTimeRange(item?.time_start, item?.time_end, selectedDate)
        : null
    const mapsLink = toMapLink(item)
    const linkLabel = item?.searchType === 'places' ? 'More info' : 'Event details'
    const images = Array.isArray(item?.images) ? item.images : (item?.image ? [item.image] : [])
    const primaryUrl = item?.url ?? (Array.isArray(item?.urls) ? item.urls.find(Boolean) : null)
    const reservationUrl = Array.isArray(item?.reservation_url)
        ? item.reservation_url.find(Boolean)
        : null
    const socialLink = Array.isArray(item?.social_link)
        ? item.social_link.find(Boolean)
        : null
    const phone = Array.isArray(item?.telephone)
        ? item.telephone.find(Boolean)
        : null
    const email = Array.isArray(item?.email)
        ? item.email.find(Boolean)
        : null
    const telHref = phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null
    const emailHref = email ? `mailto:${email}` : null

    const linkItems = []
    if (primaryUrl) {
        linkItems.push({ href: primaryUrl, label: linkLabel })
    }
    if (reservationUrl && reservationUrl !== primaryUrl) {
        linkItems.push({ href: reservationUrl, label: 'Reserve' })
    }
    if (socialLink && !linkItems.some(link => link.href === socialLink)) {
        linkItems.push({ href: socialLink, label: 'Social' })
    }

    // Generate shareable link
    const shareLink = item?.searchType === 'places'
        ? `${window.location.origin}/place/${item.id}`
        : `${window.location.origin}/event/${item.id}`

    const handleShare = async (event) => {
        event?.stopPropagation?.()
        try {
            await navigator.clipboard.writeText(shareLink)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch (err) {
            console.error('Failed to copy link:', err)
        }
    }

    const cardId = item.id

    // Action buttons component (default variant)
    const actionButtonsDefault = (
        <div className="flex items-center gap-2">
            {onShowPlanPopup && (
                <PlanButton
                    item={item}
                    itemPlanColors={itemPlanColors}
                    onShowPopup={onShowPlanPopup}
                    variant="icon"
                />
            )}
            {isSaved !== undefined && onToggleSave && (
                <SaveButton
                    item={item}
                    isSaved={isSaved}
                    onToggle={onToggleSave}
                    variant="icon"
                />
            )}
            <button
                type="button"
                onClick={handleShare}
                className={`flex-shrink-0 transition ${copySuccess ? 'text-green-600' : 'text-slate-400 hover:text-purple-600'}`}
                title="Copy shareable link"
            >
                {copySuccess ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                )}
            </button>
        </div>
    )

    // Action buttons component (hover variant with circular backgrounds)
    const actionButtonsHover = (
        <div className="flex items-center gap-3">
            {onShowPlanPopup && (
                <PlanButton
                    item={item}
                    itemPlanColors={itemPlanColors}
                    onShowPopup={onShowPlanPopup}
                    variant="hover-circle"
                />
            )}
            {isSaved !== undefined && onToggleSave && (
                <SaveButton
                    item={item}
                    isSaved={isSaved}
                    onToggle={onToggleSave}
                    variant="hover-circle"
                />
            )}
            <button
                type="button"
                onClick={handleShare}
                className={`flex-shrink-0 rounded-full bg-white/10 p-2.5 shadow-lg backdrop-blur-sm transition hover:bg-white/20 ${copySuccess ? 'text-green-600' : 'text-white'}`}
                title="Copy shareable link"
            >
                {copySuccess ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                )}
            </button>
        </div>
    )

    // Title with category (default variant - with inline actions)
    const titleWithCategory = (
        <h3 className="flex items-center gap-2 text-lg font-semibold">
            <span className={`text-xl ${item?.searchType === 'places' ? 'text-blue-600' : 'text-purple-600'}`}>
                {pinConfig.glyph}
            </span>
            <a
                href={primaryUrl || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="text-slate-900 transition hover:text-purple-600 hover:underline"
                onClick={event => event.stopPropagation()}
            >
                {item?.name ?? item?.place_name ?? 'Untitled'}
            </a>
            {variant === 'default' && (
                <div className="ml-auto flex items-center gap-2">
                    {actionButtonsDefault}
                </div>
            )}
        </h3>
    )

    // Title without inline actions (for hover variant)
    const titleSimple = (
        <h3 className="flex items-center gap-2 text-lg font-semibold">
            <span className={`text-xl ${item?.searchType === 'places' ? 'text-blue-600' : 'text-purple-600'}`}>
                {pinConfig.glyph}
            </span>
            <a
                href={primaryUrl || '#'}
                target="_blank"
                rel="noreferrer noopener"
                className="text-slate-900 transition hover:text-purple-600 hover:underline"
                onClick={event => event.stopPropagation()}
            >
                {item?.name ?? item?.place_name ?? 'Untitled'}
            </a>
        </h3>
    )

    const descriptionElement = truncatedDescription && (
        <p className="text-sm leading-relaxed text-slate-600">{truncatedDescription}</p>
    )

    // Time display with left accent border
    const timeElement = timeRange && (
        <div className="border-l-4 border-purple-500 bg-purple-50 pl-3 pr-4 py-2">
            <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-4 w-4 flex-shrink-0 text-purple-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <p className="text-sm font-medium text-purple-900">{timeRange}</p>
            </div>
        </div>
    )

    // Full address text for map link
    const fullAddressText = [item?.place_name, item?.address].filter(Boolean).join(' · ') || 'View on map'

    const contactInfo = (phone || email) && (
        <div className="space-y-1 text-sm text-slate-500">
            {phone && telHref && (
                <p>
                    Phone:{' '}
                    <a
                        href={telHref}
                        className="text-logo-pink transition hover:text-purple-600"
                        onClick={event => event.stopPropagation()}
                    >
                        {phone}
                    </a>
                </p>
            )}
            {email && emailHref && (
                <p>
                    Email:{' '}
                    <a
                        href={emailHref}
                        className="text-logo-pink transition hover:text-purple-600"
                        onClick={event => event.stopPropagation()}
                    >
                        {email}
                    </a>
                </p>
            )}
        </div>
    )

    // Filter out primary URL from additional links (it's now in title)
    const additionalLinks = linkItems.filter(link => link.href !== primaryUrl)

    const linksSection = (additionalLinks.length > 0 || mapsLink) && (
        <div className="mt-auto flex flex-wrap gap-3 pt-3 text-sm font-medium">
            {additionalLinks.map(link => (
                <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-logo-pink transition hover:text-purple-600"
                    onClick={event => event.stopPropagation()}
                >
                    {link.label}
                </a>
            ))}
            {mapsLink && (
                <a
                    href={mapsLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-1 text-logo-pink transition hover:text-purple-600"
                    onClick={event => event.stopPropagation()}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="h-5 w-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                        />
                    </svg>
                    {fullAddressText}
                </a>
            )}
        </div>
    )

    // Render based on variant
    if (variant === 'hover') {
        // Hover variant: icons hidden by default, appear at bottom of image on hover
        return (
            <button
                type="button"
                data-result-id={cardId}
                onClick={() => onSelect(cardId)}
                className={`group relative flex h-full flex-col gap-3 rounded-2xl border-2 border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 select-text ${
                    isSelected ? 'highlighted-item' : ''
                }`}
            >
                <div className="relative">
                    <ImageCarousel
                        images={images}
                        alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                        className="h-48 w-full rounded-t-xl object-cover"
                    />
                    {/* Hover overlay with action buttons at bottom */}
                    <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-3 px-4 pb-2 opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100">
                        {actionButtonsHover}
                    </div>
                </div>
                <div className="flex flex-col gap-3 p-4 pt-0">
                    {titleSimple}
                    {timeElement}
                    {descriptionElement}
                    {contactInfo}
                    {linksSection}
                </div>
            </button>
        )
    }

    // Default variant: icons always visible in header
    return (
        <button
            type="button"
            data-result-id={cardId}
            onClick={() => onSelect(cardId)}
            className={`relative flex h-full flex-col gap-3 rounded-2xl border-2 border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 select-text ${
                isSelected ? 'highlighted-item' : ''
            }`}
        >
            <ImageCarousel
                images={images}
                alt={item?.name ? `${item.name} artwork` : 'Listing artwork'}
                className="h-48 w-full rounded-t-xl object-cover"
            />
            <div className="flex flex-col gap-3 p-4 pt-0">
                {titleWithCategory}
                {timeElement}
                {descriptionElement}
                {contactInfo}
                {linksSection}
            </div>
        </button>
    )
}
